/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { BOTTOM_NAVIGATION_HEIGHT } from "../../../util/consts";

/**
 * Opening a notes field moves it three times: on focus, again when the field itself settles
 * (the multiline input autosizes to `minRows` and the helper text renders a frame or two later)
 * and again when the native shell shrinks the layout viewport for the keyboard. Every trigger is
 * debounced onto this one timer so the burst collapses into a single correction measured against
 * the settled geometry, instead of guessing how long the resize takes.
 */
const NOTES_SCROLL_SETTLE_MS = 200;

/**
 * `buttonBoxHeight` is `offsetHeight` — whole pixels measured against a fractional laid-out height —
 * so the reserved clearance under-shoots by a sub-pixel and the settled field sits a fraction of a
 * pixel inside its own scroll margin. Anything within a pixel counts as clear, so the correction
 * does not re-scroll a field it has already placed.
 */
const NOTES_SCROLL_TOLERANCE_PX = 1;

/**
 * The box `scrollIntoView` scrolls the field into: the padding box of its nearest scrollable
 * ancestor, which is `#main-scroll-container` here — the app scrolls inside that element rather than
 * the document. Falls back to the layout viewport when nothing above the field scrolls.
 */
const scrollportOf = (element: HTMLElement): { top: number; bottom: number } => {
  for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
    const { overflowY } = window.getComputedStyle(ancestor);
    if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
      const top = ancestor.getBoundingClientRect().top + ancestor.clientTop;
      return { top, bottom: top + ancestor.clientHeight };
    }
  }
  return { top: 0, bottom: window.innerHeight };
};

/**
 * Chromium's Blink engine decides whether `block: "nearest"` has to scroll from the field's bare
 * border box and only applies `scroll-margin` to the alignment once it has already decided to.
 * Below `sm` the field lands inside the scrollport but behind the fixed button box, so `nearest`
 * calls it visible, computes no scroll, and the reserved clearance is never applied — measured on
 * the emulator, the bottom 39px of the internal notes field stayed under the button box no matter
 * how large the margin was made. Choosing the alignment from the field's scroll-margin box
 * restores the intent: `end` when the reserved clearance is encroached, `start` when the label has
 * gone off the top, and `nearest` — a no-op — once the field is clear, so the page never moves
 * under the caret.
 */
const notesFieldAlignment = (notesField: HTMLElement): ScrollLogicalPosition => {
  const scrollport = scrollportOf(notesField);
  const { top, bottom } = notesField.getBoundingClientRect();
  const { scrollMarginTop, scrollMarginBottom } = window.getComputedStyle(notesField);

  if (top - parseFloat(scrollMarginTop) < scrollport.top - NOTES_SCROLL_TOLERANCE_PX) {
    return "start";
  }
  if (bottom + parseFloat(scrollMarginBottom) > scrollport.bottom + NOTES_SCROLL_TOLERANCE_PX) {
    return "end";
  }
  return "nearest";
};

/** The props a notes field wrapper spreads to keep itself clear of the fixed button box. */
export interface NotesFieldScrollProps {
  scrollMarginBottom: number;
  onFocus: (event: React.FocusEvent<HTMLDivElement>) => void;
  onBlur: () => void;
}

/**
 * Keeps a focused notes field — label, input and helper text — clear of the fixed button box and the
 * bottom navigation, correcting the scroll as the field autosizes and as the keyboard resizes the
 * viewport. `buttonBoxHeight` is the measured height of the fixed button box the field has to clear.
 */
export function useNotesFieldScrollCorrection(buttonBoxHeight: number): NotesFieldScrollProps {
  const trackingRef = useRef<{
    resizeObserver: ResizeObserver;
    correctScroll: () => void;
  } | null>(null);

  /**
   * Scrolls the whole field — label, input and helper text — only as far as it takes to clear the
   * scrollport and the clearance reserved as its `scroll-margin-bottom`.
   */
  const scrollNotesFieldIntoView = useMemo(
    () =>
      debounce((notesField: HTMLElement) => {
        notesField.scrollIntoView({ behavior: "smooth", block: notesFieldAlignment(notesField) });
      }, NOTES_SCROLL_SETTLE_MS),
    []
  );

  const stopTrackingNotesField = useCallback(() => {
    scrollNotesFieldIntoView.cancel();
    if (trackingRef.current) {
      trackingRef.current.resizeObserver.disconnect();
      window.removeEventListener("resize", trackingRef.current.correctScroll);
      trackingRef.current = null;
    }
  }, [scrollNotesFieldIntoView]);

  /**
   * `focusin` bubbles, so `currentTarget` is already the whole field the correction scrolls — no
   * marker attribute, and no ref for the field's own `autoFocus` to outrun.
   */
  const startTrackingNotesField = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      const notesField = event.currentTarget;
      const correctScroll = () => scrollNotesFieldIntoView(notesField);

      const resizeObserver = new ResizeObserver(correctScroll);
      resizeObserver.observe(notesField);
      window.addEventListener("resize", correctScroll);
      trackingRef.current = { resizeObserver, correctScroll };

      correctScroll();
    },
    [scrollNotesFieldIntoView]
  );

  useEffect(() => stopTrackingNotesField, [stopTrackingNotesField]);

  /**
   * `ButtonBox` is fixed directly above the bottom navigation, so the two together are the whole
   * obstruction the field has to clear. `ReferenceNoteWrapper` adds the navigation's safe-area
   * inset to this clearance in CSS, the same way `ButtonBox` anchors above it.
   */
  return useMemo(
    () => ({
      scrollMarginBottom: buttonBoxHeight + BOTTOM_NAVIGATION_HEIGHT,
      onFocus: startTrackingNotesField,
      onBlur: stopTrackingNotesField,
    }),
    [buttonBoxHeight, startTrackingNotesField, stopTrackingNotesField]
  );
}
